// Post-login interstitial asking for today's time budget. Shown once per login session across tabs and
// again after any org switch; clinicians only. A first run shows onboarding instead.
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  effect,
  inject,
  signal
} from '@angular/core'
import { animate, style, transition, trigger } from '@angular/animations'
import { NavigationEnd, Router } from '@angular/router'
import { Store } from '@ngxs/store'
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop'
import { distinctUntilChanged, filter, map, take } from 'rxjs'
import { RouteName } from '../../../core/enums/route-name.enum'
import { UserState } from '../../../store/user/user.state'
import { AuthState } from '../../../store/auth/auth.state'
import { PaRoleResolverService } from '../data/pa-role-resolver.service'
import { PaSessionService } from '../session/pa-session.service'
import { PaShellReadyService } from '../session/pa-shell-ready.service'
import { isMyPlanUrl } from '../session/my-plan-url'
import { OrgScopeService } from '../org-scope/org-scope.service'
import { TimeModeService } from '../time-mode/time-mode.service'
import { isSetupDone } from '../onboarding/pa-setup-done.storage'
import { PaTimePickerComponent } from '../ui/pa-time-picker/pa-time-picker.component'
import { PaOrgPickerComponent } from '../org-scope/pa-org-picker.component'
import { TimeModeValue } from '../ui/pa-ui.types'
import { PREFERS_REDUCED_MOTION } from '../ui/pa-reduced-motion'

@Component({
  selector: 'aiomed-pa-daily-briefing',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [PaTimePickerComponent, PaOrgPickerComponent],
  animations: [
    trigger('pageFade', [
      transition(':enter', [
        style({ opacity: 0 }),
        animate('200ms ease-out', style({ opacity: 1 }))
      ])
    ]),
    trigger('contentRise', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(10px)' }),
        animate(
          '220ms 40ms cubic-bezier(0.16, 1, 0.3, 1)',
          style({ opacity: 1, transform: 'none' })
        )
      ])
    ])
  ],
  templateUrl: './daily-briefing.component.html',
  styleUrls: ['./daily-briefing.component.scss']
})
export class DailyBriefingComponent {
  private readonly roleResolver = inject(PaRoleResolverService)
  private readonly session = inject(PaSessionService)
  private readonly shellReady = inject(PaShellReadyService)
  private readonly orgScope = inject(OrgScopeService)
  protected readonly timeMode = inject(TimeModeService)
  private readonly store = inject(Store)
  private readonly router = inject(Router)

  // Reduced motion disables the page reveal.
  protected readonly reduceMotion = PREFERS_REDUCED_MOTION
  protected readonly visible = signal(false)
  // Wait for the app shell, so the overlay never covers the bare initial loader and the sidebar shows beside it.
  protected readonly show = computed(
    () => this.visible() && this.shellReady.ready()
  )
  // Set once the open briefing has reached /my-plan. Only a navigation after that is the user leaving.
  private onPlan = false
  // Reactive: the user store can populate after the briefing is already showing.
  protected readonly name = toSignal(
    this.store
      .select(UserState.currentUser)
      .pipe(
        map(
          user =>
            (user as { name?: { firstName?: string } } | null)?.name
              ?.firstName ?? ''
        )
      ),
    { initialValue: '' }
  )
  protected readonly now = signal(new Date())
  // Null until the profile loads after login. The admin check depends on it, so tryShow() waits for it.
  private readonly userType = toSignal(
    this.store
      .select(UserState.currentUser)
      .pipe(
        map(user => (user as { _userType?: string } | null)?._userType ?? null)
      ),
    { initialValue: null }
  )
  // Part of the setup-done key the onboarding guard below reads, so this briefing defers to onboarding
  // for THIS clinician rather than for whoever last used the browser.
  private readonly userId = toSignal(
    this.store.select(UserState.currentUser).pipe(
      map(user => {
        const u = user as { id?: string; _id?: string } | null
        return u?.id ?? u?._id ?? null
      })
    ),
    { initialValue: null as string | null }
  )

  protected readonly orgName = computed(() => this.orgScope.activeOrgName())

  protected readonly greeting = computed(() => {
    const h = this.now().getHours()
    return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening'
  })

  constructor() {
    // The overlay scrolls on its own, so lock the page behind it while open; otherwise platforms with
    // persistent scrollbars paint two bars side by side.
    effect(() => {
      const open = this.show()
      try {
        // Both elements: locking body alone leaves the root element scrollable.
        document.documentElement.style.overflow = open ? 'hidden' : ''
        document.body.style.overflow = open ? 'hidden' : ''
      } catch {
        /* SSR / no document - nothing to lock. */
      }
    })
    inject(DestroyRef).onDestroy(() => {
      try {
        document.documentElement.style.overflow = ''
        document.body.style.overflow = ''
      } catch {
        /* no document */
      }
    })
    // Org switch clears the session flag (via PaSessionService.reset) → re-show for the new org.
    this.session.reset$
      .pipe(takeUntilDestroyed())
      .subscribe(() => this.tryShow())
    // Mounted once in the app shell and driven by auth state, so a transient render cannot consume the
    // once-per-session flag.
    this.store
      .select(AuthState.isAuthenticated)
      .pipe(distinctUntilChanged(), takeUntilDestroyed())
      .subscribe(authed => {
        if (authed) {
          this.tryShow()
        } else {
          // Logout. This is the only always-mounted PA hook, so reset(null) here is what clears session
          // state, including the PHI scrubber's identity map.
          this.visible.set(false)
          this.session.reset(null)
        }
      })
    // Re-evaluate once the org id and the profile have both resolved (a tick after login); the auth
    // event alone fires too early to read the per-org setup flag. tryShow() is idempotent.
    effect(() => {
      const userType = this.userType() // tracked: re-runs when the profile loads
      if (this.orgScope.activeOrgId() != null && userType != null) {
        this.tryShow()
      }
    })
    // The sidebar stays clickable beside the overlay. Leaving /my-plan from it (or with Back) would load
    // the new page under the briefing, so close it there as "Skip for now" does.
    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed()
      )
      .subscribe(e => {
        if (!this.visible()) {
          return
        }
        if (isMyPlanUrl(e.urlAfterRedirects)) {
          this.onPlan = true
        } else if (this.onPlan) {
          this.visible.set(false)
        }
      })
  }

  /** Show once per session for a clinician (flag on, not admin). Admin/flag-off never shows. */
  private tryShow(): void {
    // The logout reset re-enters here via reset$ on the login screen, where the resolver would default
    // to a clinician.
    if (!this.store.selectSnapshot(AuthState.isAuthenticated)) {
      return
    }
    // Fail closed until the profile loads: before that an admin cannot be told from a clinician. The
    // effect above re-runs this once it does.
    if (this.userType() == null) {
      return
    }
    // Once per login session, browser-wide: the flag is in localStorage, so a new tab does not re-brief.
    // Only logout and an org switch clear it (a switch back to an org seen earlier re-briefs too).
    if (this.session.isBriefingShown()) {
      return
    }
    // First run: onboarding shows instead.
    if (!isSetupDone(this.userId(), this.orgScope.activeOrgId())) {
      return
    }
    this.roleResolver
      .resolve$()
      .pipe(take(1))
      .subscribe(resolution => {
        if (!resolution.isAssistantVisible) {
          // Folds the whole gate: RPM type, env flag and per-user allowlist.
          return
        }
        this.now.set(new Date())
        // Already on /my-plan, the navigate below is a same-URL no-op that emits no NavigationEnd.
        this.onPlan = isMyPlanUrl(this.router.url)
        this.visible.set(true)
        this.session.markBriefingShown()
        // Overlay /my-plan so the sidebar's Home entry is active behind it.
        this.router.navigate(['/' + RouteName.MyPlan])
      })
  }

  protected onTime(value: TimeModeValue): void {
    this.timeMode.set(value)
  }

  protected skip(): void {
    this.visible.set(false)
    this.router.navigate(['/' + RouteName.Patients])
  }

  protected seePlan(): void {
    this.visible.set(false)
    this.router.navigate(['/' + RouteName.MyPlan])
  }
}
